import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IonIcon } from "@ionic/react";
import { closeOutline, personOutline, searchOutline, closeCircle } from "ionicons/icons";
import { 
  fetchRecentPublicFilesFromFirestore, 
  searchPublicFilesWithUrls, 
  searchCreatorFilesWithUrls 
} from "../../../../lib/firebase/firebase";
import { isEmbroideryTrace } from "../../../../config/appVariant";
// Popular keywords are now curated (see useEffect below)
import { 
  setCommunityFiles, 
  appendCommunityFiles, 
  setCurrentPage, 
  setHasMore, 
  setTotalHits,
  setIsSearching,
  setSearchQuery,
  clearSearch,
  clearCreatorFilter
} from "../../../../redux/slices/CommunityFiles";
import { EmptyState } from "./EmptyState";
import Card from "../Card";
import SkeletonGrid from "./SkeletonCard";
import "./styles.css";

const Community = () => {
  const dispatch = useDispatch();
  const { files, currentPage, hasMore, totalHits, searchQuery, isSearching, creatorFilter, isFilteringByCreator } = useSelector(
    (state) => state.communityFiles
  );
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [lastDoc, setLastDoc] = useState(null); // For Firestore cursor-based pagination
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery || "");
  const [popularKeywords, setPopularKeywords] = useState([]);
  const searchInputRef = useRef(null);
  const observerRef = useRef();
  const ITEMS_PER_PAGE = 36; // Balanced for fast initial load + good content
  const MIN_LOADING_TIME = 500; // Minimum time to show skeleton (ms)

  useEffect(() => {
    if (files.length === 0 && isInitialLoad) {
      getCommunityFiles();
    } else if (files.length > 0 && isInitialLoad) {
      // Ensure skeleton shows for at least MIN_LOADING_TIME
      const timer = setTimeout(() => {
        setIsInitialLoad(false);
        setShowSkeleton(false);
      }, MIN_LOADING_TIME);
      return () => clearTimeout(timer);
    }
  }, [files.length, isInitialLoad]);

  // Curated popular keywords (based on database analysis)
  useEffect(() => {
    // Handpicked keywords: funny/playful + seasonal + popular categories.
    // SvgTrace counts come from analysing its live index; EmbroideryTrace's
    // gallery is new, so its keywords ship without counts rather than fake ones.
    const curatedKeywords = isEmbroideryTrace
      ? [
          { keyword: 'monogram' },
          { keyword: 'floral' },
          { keyword: 'animal' },
          { keyword: 'holiday' },
          { keyword: 'applique' },
          { keyword: 'sports' },
          { keyword: 'baby' },
          { keyword: 'quote' },
          { keyword: 'heart' },
          { keyword: 'nature' },
          { keyword: 'vintage' },
          { keyword: 'cute' },
        ]
      : [
          { keyword: 'cute', count: 217 },
          { keyword: 'funny', count: 117 },
          { keyword: 'christmas', count: 100 },
          { keyword: 'halloween', count: 57 },
          { keyword: 'sports', count: 211 },
          { keyword: 'animal', count: 149 },
          { keyword: 'floral', count: 85 },
          { keyword: 'dog', count: 41 },
          { keyword: 'holiday', count: 85 },
          { keyword: 'vintage', count: 52 },
          { keyword: 'heart', count: 45 },
          { keyword: 'nature', count: 48 },
        ];
    setPopularKeywords(curatedKeywords);
  }, []);

  // Handle creator filter changes
  useEffect(() => {
    if (isFilteringByCreator && creatorFilter?.uid) {
      getCreatorFiles(creatorFilter.uid);
    }
  }, [isFilteringByCreator, creatorFilter?.uid]);

  const getCommunityFiles = async () => {
    try {
      setLoading(true);
      const startTime = Date.now();
      // Use Firestore directly for faster, real-time data
      const result = await fetchRecentPublicFilesFromFirestore(ITEMS_PER_PAGE, null);
      dispatch(setCommunityFiles(result.files));
      dispatch(setCurrentPage(0));
      dispatch(setHasMore(result.hasMore));
      dispatch(setTotalHits(result.totalHits));
      setLastDoc(result.lastDoc); // Store cursor for pagination
      
      // Ensure skeleton shows for at least MIN_LOADING_TIME
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);
      
      setTimeout(() => {
        setLoading(false);
        setIsInitialLoad(false);
        setShowSkeleton(false);
      }, remainingTime);
    } catch (error) {
      console.error("error getting community files: ", error);
      setLoading(false);
      setIsInitialLoad(false);
      setShowSkeleton(false);
    }
  };

  const getCreatorFiles = async (creatorUid) => {
    try {
      console.log("👨‍🎨 Getting creator files for:", creatorUid);
      setLoading(true);
      setLastDoc(null); // Reset Firestore cursor when switching to creator filter
      // Creator filter uses Algolia for filtering by ownerUid
      const result = await searchCreatorFilesWithUrls(creatorUid, 0, ITEMS_PER_PAGE);
      dispatch(setCommunityFiles(result.files));
      dispatch(setCurrentPage(0));
      dispatch(setHasMore(result.hasMore));
      dispatch(setTotalHits(result.totalHits));
      console.log("✅ Creator files loaded successfully");
    } catch (error) {
      console.error("❌ Error getting creator files:", error);
      // Set fallback state
      dispatch(setCommunityFiles([]));
      dispatch(setHasMore(false));
      dispatch(setTotalHits(0));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    if (!query || query.trim().length === 0) return;
    
    try {
      console.log("🔍 Community search starting for:", query);
      setLoading(true);
      dispatch(setSearchQuery(query));
      dispatch(setIsSearching(true));
      dispatch(clearCreatorFilter()); // Clear creator filter when searching
      setLastDoc(null); // Reset Firestore cursor when switching to search
      
      // Search uses Algolia for full-text search capabilities
      const result = await searchPublicFilesWithUrls(query, 0, ITEMS_PER_PAGE);
      console.log("📊 Community search result:", result);
      
      dispatch(setCommunityFiles(result.files));
      dispatch(setCurrentPage(0));
      dispatch(setHasMore(result.hasMore));
      dispatch(setTotalHits(result.totalHits));
      
      console.log("✅ Community search completed successfully");
    } catch (error) {
      console.error("❌ Error searching community files:", error);
      // Set some fallback state to prevent infinite loading
      dispatch(setCommunityFiles([]));
      dispatch(setHasMore(false));
      dispatch(setTotalHits(0));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    handleSearch(localSearchQuery);
  };

  const handleKeywordClick = (keyword) => {
    setLocalSearchQuery(keyword);
    handleSearch(keyword);
  };

  const handleClearSearch = async () => {
    try {
      setLoading(true);
      setLocalSearchQuery("");
      dispatch(clearSearch());
      dispatch(clearCreatorFilter()); // Also clear creator filter
      // Use Firestore directly for faster, real-time data
      const result = await fetchRecentPublicFilesFromFirestore(ITEMS_PER_PAGE, null);
      dispatch(setCommunityFiles(result.files));
      dispatch(setCurrentPage(0));
      dispatch(setHasMore(result.hasMore));
      dispatch(setTotalHits(result.totalHits));
      setLastDoc(result.lastDoc); // Store cursor for pagination
    } catch (error) {
      console.error("error getting community files: ", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearCreatorFilter = async () => {
    try {
      setLoading(true);
      dispatch(clearCreatorFilter());
      dispatch(setIsSearching(false)); // Also clear search
      // Use Firestore directly for faster, real-time data
      const result = await fetchRecentPublicFilesFromFirestore(ITEMS_PER_PAGE, null);
      dispatch(setCommunityFiles(result.files));
      dispatch(setCurrentPage(0));
      dispatch(setHasMore(result.hasMore));
      dispatch(setTotalHits(result.totalHits));
      setLastDoc(result.lastDoc); // Store cursor for pagination
    } catch (error) {
      console.error("error getting community files: ", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMoreData = async () => {
    if (loading || isLoadingMore || !hasMore) {
      console.log(`⏸️ FETCH MORE: Skipping - loading: ${loading}, isLoadingMore: ${isLoadingMore}, hasMore: ${hasMore}`);
      return;
    }

    try {
      setIsLoadingMore(true);
      const nextPage = currentPage + 1;
      let result;
      
      console.log(`🔄 FETCH MORE: Loading page ${nextPage}`);
      console.log(`🔄 FETCH MORE: Current state:`, {
        isFilteringByCreator,
        creatorFilter,
        isSearching,
        currentPage,
        hasMore,
        totalFiles: files.length
      });
      
      if (isFilteringByCreator && creatorFilter?.uid) {
        // Creator filter uses Algolia (page-based)
        console.log(`👨‍🎨 FETCH MORE: Loading creator files for ${creatorFilter.uid}, page ${nextPage}`);
        result = await searchCreatorFilesWithUrls(creatorFilter.uid, nextPage, ITEMS_PER_PAGE);
        console.log(`👨‍🎨 FETCH MORE: Got ${result.files.length} creator files, hasMore: ${result.hasMore}`);
        
        // Verify that ALL returned files are from the correct creator
        const correctCreator = result.files.every(file => file.ownerUid === creatorFilter.uid);
        console.log(`👨‍🎨 FETCH MORE: All files from correct creator: ${correctCreator}`);
        
        if (!correctCreator) {
          console.error(`❌ FETCH MORE: Found files from wrong creators!`);
          result.files.forEach((file, index) => {
            if (file.ownerUid !== creatorFilter.uid) {
              console.error(`   File ${index}: Expected ${creatorFilter.uid}, got ${file.ownerUid}`);
            }
          });
        }
        dispatch(setCurrentPage(nextPage));
      } else if (isSearching) {
        // Search uses Algolia (page-based)
        console.log(`🔍 FETCH MORE: Loading search results, page ${nextPage}`);
        result = await searchPublicFilesWithUrls(searchQuery, nextPage, ITEMS_PER_PAGE);
        console.log(`🔍 FETCH MORE: Got ${result.files.length} search results`);
        dispatch(setCurrentPage(nextPage));
      } else {
        // Default view uses Firestore (cursor-based)
        console.log(`📦 FETCH MORE: Loading public files from Firestore with cursor`);
        result = await fetchRecentPublicFilesFromFirestore(ITEMS_PER_PAGE, lastDoc);
        console.log(`📦 FETCH MORE: Got ${result.files.length} public files from Firestore`);
        setLastDoc(result.lastDoc); // Update cursor for next fetch
      }
      
      if (result.files.length > 0) {
        console.log(`✅ FETCH MORE: Appending ${result.files.length} files`);
        dispatch(appendCommunityFiles(result.files));
        dispatch(setHasMore(result.hasMore));
        dispatch(setTotalHits(result.totalHits || totalHits));
      } else {
        console.log(`🛑 FETCH MORE: No more files available`);
        dispatch(setHasMore(false));
      }
      
    } catch (error) {
      console.error("❌ Error fetching more data:", error);
      dispatch(setHasMore(false));
    } finally {
      setIsLoadingMore(false);
    }
  };

  const communityFiles = useMemo(() => {
    return files.map((file) => {
      const dateObj = file.createdAt
        ? new Date(file.createdAt.seconds * 1000)
        : null;
      const date = dateObj && !isNaN(dateObj.getTime())
        ? dateObj.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })
        : undefined;
      return {
        ...file,
        timestamp: dateObj,
        date,
      };
    });
  }, [files]);

  const lastElementRef = useCallback(
    (node) => {
      if (loading || isLoadingMore) return;

      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          fetchMoreData();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [loading, isLoadingMore, hasMore]
  );

  const handleImageLoaded = () => {
    console.log("Image loaded");
  };

  // Search bar JSX - stored as variable to reuse without re-mounting
  const searchBarJSX = (
    <div className="community-search-container">
      <form onSubmit={handleSearchSubmit} className="community-search-form">
        <div className="community-search-input-wrapper">
          <IonIcon icon={searchOutline} className="community-search-icon" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder={
              isEmbroideryTrace
                ? "Search designs... (e.g. cat, monogram, floral)"
                : "Search SVGs... (e.g. cat, flowers, vintage)"
            }
            value={localSearchQuery}
            onChange={(e) => setLocalSearchQuery(e.target.value)}
            className="community-search-input"
          />
          {localSearchQuery && (
            <button
              type="button"
              onClick={() => {
                setLocalSearchQuery("");
                if (isSearching) handleClearSearch();
              }}
              className="community-search-clear"
            >
              <IonIcon icon={closeCircle} />
            </button>
          )}
        </div>
        <button type="submit" className="community-search-button" disabled={loading}>
          Search
        </button>
      </form>
      
      {/* Popular Keywords — temporarily hidden, to be restored
      {popularKeywords.length > 0 && !isSearching && !isFilteringByCreator && (
        <div className="community-keywords">
          <span className="community-keywords-label">Popular:</span>
          {popularKeywords.slice(0, 8).map(({ keyword, count }) => (
            <button
              key={keyword}
              onClick={() => handleKeywordClick(keyword)}
              className="community-keyword-tag"
              title={count ? `${count} designs` : undefined}
            >
              {keyword}
            </button>
          ))}
        </div>
      )}
      */}

      {/* Search Results Info */}
      {isSearching && (
        <div className="community-search-results-info">
          <span>
            Found <strong>{totalHits}</strong> results for "<strong>{searchQuery}</strong>"
          </span>
          <button onClick={handleClearSearch} className="community-clear-search">
            Clear search
          </button>
        </div>
      )}
    </div>
  );

  // Show skeleton while loading initial data or on page refresh
  if ((loading && communityFiles.length === 0) || showSkeleton) {
    return (
      <div className="all-container">
        {searchBarJSX}
        <SkeletonGrid count={20} />
      </div>
    );
  }

  if (communityFiles.length === 0 && !loading) {
    return (
      <div className="all-container">
        {searchBarJSX}
        
        {/* Creator filter indicator for empty state */}
        {isFilteringByCreator && creatorFilter && (
          <div className="creator-filter-indicator">
            <div className="creator-filter-content">
              <IonIcon icon={personOutline} />
              <span>Showing artwork by: <strong>{creatorFilter.displayName}</strong></span>
              <button 
                className="creator-filter-clear"
                onClick={handleClearCreatorFilter}
                title="Clear creator filter"
              >
                <IonIcon icon={closeOutline} />
              </button>
            </div>
          </div>
        )}
        
        {isFilteringByCreator && creatorFilter ? (
          <div className="search-no-results">
            <h3>No artwork found by {creatorFilter.displayName}</h3>
            <p>This creator hasn't shared any public designs yet.</p>
          </div>
        ) : isSearching ? (
          <div className="search-no-results">
            <h3>No results found for "{searchQuery}"</h3>
            <p>Try different keywords or browse the popular tags above.</p>
          </div>
        ) : (
          <EmptyState />
        )}
      </div>
    );
  }

  return (
    <div className="all-container">
      {searchBarJSX}

      {/* Creator filter indicator */}
      {isFilteringByCreator && creatorFilter && (
        <div className="creator-filter-indicator">
          <div className="creator-filter-content">
            <IonIcon icon={personOutline} />
            <span>Showing artwork by: <strong>{creatorFilter.displayName}</strong></span>
            <button 
              className="creator-filter-clear"
              onClick={handleClearCreatorFilter}
              title="Clear creator filter"
            >
              <IonIcon icon={closeOutline} />
            </button>
          </div>
        </div>
      )}
      
      <div className="cards-container">
        <div className="inner-container">
          {communityFiles.filter(file => file && file.uri).map((file, index) => (
            <div key={`${file.id}-${index}`}>
              <Card file={file} index={index} onImageLoaded={handleImageLoaded} />
            </div>
          ))}

          {hasMore && (
            <div
              ref={lastElementRef}
              style={{
                gridColumn: "1 / -1",
              }}
            >
              {isLoadingMore && (
                <div className="skeleton-loading-indicator">
                  Loading more images<span className="loading-dots"></span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Community;
